import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { HiExclamationTriangle, HiXMark } from "react-icons/hi2";
import ParcelOrderService from "../services/parcel-order.service";
import { clearPendingOrder, readPendingOrder } from "../utils/pending-order";
import { reportPurchaseConversion } from "../utils/conversion";

// PayFast's cancel link returns customers to the homepage (or the business
// dashboard for bulk orders) with nothing to say the order is still unpaid.
const PAGES_WITH_OWN_PAYMENT_HANDLING = ["/payment-success", "/cart"];

export default function PendingPaymentNotice() {
  const { pathname } = useLocation();
  const [unpaidOrder, setUnpaidOrder] = useState(null);
  const skipCheck = PAGES_WITH_OWN_PAYMENT_HANDLING.includes(pathname);

  useEffect(() => {
    if (skipCheck) return undefined;
    const pendingOrder = readPendingOrder();
    if (!pendingOrder) return undefined;
    let isCurrent = true;

    ParcelOrderService.findOrder(pendingOrder.reference)
      .then((order) => {
        if (!isCurrent) return;
        if (order?.status === "paid") {
          reportPurchaseConversion(pendingOrder);
          clearPendingOrder();
        } else if (order?.status === "created" && (order.delivery_status ?? "pending") === "pending") {
          setUnpaidOrder({
            reference: pendingOrder.reference,
            paymentUrl: order.payment_url || pendingOrder.paymentUrl,
          });
        } else {
          clearPendingOrder();
        }
      })
      .catch(() => {});

    return () => {
      isCurrent = false;
    };
  }, [skipCheck]);

  if (!unpaidOrder || skipCheck) return null;

  const dismiss = () => {
    clearPendingOrder();
    setUnpaidOrder(null);
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200" role="status">
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <HiExclamationTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-amber-900">
            Your payment for order <span className="font-semibold">#{unpaidOrder.reference}</span> wasn't completed.
            The order is saved, but it won't be collected until it's paid.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {unpaidOrder.paymentUrl && (
            <a
              href={unpaidOrder.paymentUrl}
              className="text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 px-4 py-1.5 rounded-lg transition-colors"
            >
              Pay now
            </a>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="inline-flex items-center gap-1 text-sm font-medium text-amber-800 hover:text-amber-950 px-2 py-1.5 rounded-lg hover:bg-amber-100 transition-colors"
          >
            <HiXMark className="h-4 w-4" aria-hidden="true" />
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
